import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { buildJobRunDetailPath } from '@/features/jobs/utils/jobPaths';
import { mapPipelineRunSummaryItems } from '@/features/pipelines/utils/pipelineRunSummaryItems';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { mapPipelineStageSummaryItems } from '@/features/pipelines/utils/stageSummaryLinks';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import { TableBuildStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableBuildStatusBreakdownCell';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { TableStageStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStageStatusBreakdownCell';
import { resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import {
  resolveCiStatusLabelKey,
  resolvePipelineLatestRunDisplayStatus,
} from '@/features/shared/utils/ciStatus';
import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getStatusCopy } from '@/shared/copy/status';
import {
  Box,
  BuildsIcon,
  formatDuration,
  formatFrequency,
  formatPercent,
  formatStatusLabel,
  resolveStatusChipTone,
} from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { getThresholdChipKey, TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { StagesIcon } from '@/ui/primitives';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { BuildStatus, MetricThresholdGroup, PipelineListItemDto } from '@flowqa/shared';

type PipelinesColumnsOptions = {
  selectedProjectId: string | null;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  tagColorByName?: Partial<Record<string, TagColor>>;
  onStatusSelect?: (status: BuildStatus | 'none') => void;
  showDeltas?: boolean;
  includeActionsColumn?: boolean;
  onEditRow?: (row: PipelineListItemDto) => void;
  onDeleteRow?: (row: PipelineListItemDto) => void;
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
};

const columnWidths = tableColumnWidths.pipelines;

const mapPipelineLatestJobSummaryItems = (row: PipelineListItemDto): SummaryTooltipItem[] =>
  (row.latestJobSummaries ?? []).map((build) => {
    const runNumber = resolveBuildRunNumber(build.buildKey ?? null);
    const pipelineSlug = resolvePipelineSlug(row);

    return {
      key: build.id,
      detailLabel: build.jobName ?? row.name,
      executionLabel: runNumber === null ? null : `#${runNumber}`,
      href: `/pipelines/${pipelineSlug}`,
      executionHref:
        row.id && build.stageName && build.jobName
          ? buildJobRunDetailPath({
              jobRunId: build.id,
              pipelineId: row.id,
              stageName: build.stageName,
              jobName: build.jobName,
            })
          : runNumber === null
            ? null
            : `/pipelines/${pipelineSlug}/runs/${runNumber}`,
      runNumber,
      status: build.status,
      timestamp: build.timestamp,
    };
  });

export const buildPipelinesColumns = ({
  selectedProjectId,
  thresholds,
  resolveThresholds,
  tagColorByName = {},
  onStatusSelect,
  showDeltas = true,
  includeActionsColumn = false,
  onEditRow,
  onDeleteRow,
  editRowPendingId = null,
  deleteRowPendingId = null,
}: PipelinesColumnsOptions): Array<GridColDef<PipelineListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: PipelineListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };

  const nameColumn: GridColDef<PipelineListItemDto> = {
    field: 'name',
    headerName: getDetailLabel('pipeline'),
    flex: columnWidths.name.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-name-cell',
    filterOperators: stringOperators,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => (
      <TableCellContainer gap="none">
        <TableNameCell
          primary={{
            label: params.row.name,
            href: `/pipelines/${resolvePipelineSlug(params.row)}`,
          }}
          tags={resolveEntityTags(params.row).map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, tagColorByName),
          }))}
        />
      </TableCellContainer>
    ),
  };

  const latestStatusColumn: GridColDef<PipelineListItemDto> = {
    field: 'latestRunStatus',
    headerName: getChartTitle('latestStatus'),
    flex: columnWidths.latestStatus.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-status-meta-cell',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const latestRun = params.row.latestRun ?? null;
      const status = resolvePipelineLatestRunDisplayStatus(latestRun);
      const statusLabel = status ? formatStatusLabel(resolveCiStatusLabelKey(status)) : 'No Runs';
      const statusTone = resolveStatusChipTone(status);
      const StatusIcon = status ? getBuildStatusIconComponent(status) : null;
      const durationLabel =
        latestRun?.durationMs !== null && latestRun?.durationMs !== undefined
          ? formatDuration(latestRun.durationMs)
          : '';
      const timestamp = latestRun?.timestamp ?? null;
      const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
      const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      const runNumber = resolveBuildRunNumber(latestRun?.buildKey ?? null);
      const runHref =
        runNumber !== null && status
          ? `/pipelines/${resolvePipelineSlug(params.row)}/runs/${runNumber}`
          : null;

      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={statusLabel}
            statusHref={runHref}
            onStatusClick={(event) => {
              event.stopPropagation();
              if (!status || runHref) return;
              onStatusSelect?.(latestRun?.status ?? 'none');
            }}
            chipTone={statusTone}
            chipIcon={StatusIcon ? <Box component={StatusIcon} /> : <Box />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={durationLabel}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={timestampLabel}
            timestampTitle={timestampTitle}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const buildsColumn: GridColDef<PipelineListItemDto> = {
    field: 'latestJobStatusCounts',
    headerName: 'Latest Jobs',
    flex: columnWidths.latestJobs.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const total = params.row.latestJobTotal ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableBuildStatusBreakdownCell
            total={total}
            counts={params.row.latestJobStatusCounts}
            summaryItems={mapPipelineLatestJobSummaryItems(params.row)}
            totalIcon={StagesIcon}
            showDetailLabel
            showDateHeaders={false}
          />
        </TableCellContainer>
      );
    },
  };

  const stagesColumn: GridColDef<PipelineListItemDto> = {
    field: 'stageStatusCounts',
    headerName: 'Latest Stages',
    flex: columnWidths.latestStages.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const total = params.row.stageTotal ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableStageStatusBreakdownCell
            total={total}
            counts={params.row.stageStatusCounts}
            summaryItems={mapPipelineStageSummaryItems(params.row)}
          />
        </TableCellContainer>
      );
    },
  };

  const runsColumn: GridColDef<PipelineListItemDto> = {
    field: 'buildStatusCounts',
    headerName: 'Runs',
    flex: columnWidths.runs.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const total = params.row.buildTotal ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableBuildStatusBreakdownCell
            total={total}
            counts={params.row.buildStatusCounts}
            summaryItems={mapPipelineRunSummaryItems(params.row)}
            totalLabelSingular="pipeline"
            totalLabelPlural="pipelines"
            totalIcon={BuildsIcon}
            showDateHeaders
          />
        </TableCellContainer>
      );
    },
  };

  const metricsColumn: GridColDef<PipelineListItemDto> = {
    field: 'metricsSummary',
    headerName: getDetailLabel('metrics'),
    flex: columnWidths.metrics.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const passRate = params.row.metrics?.passRate ?? null;
      const passRateDelta = deltasEnabled ? (params.row.metrics?.delta?.passRate ?? null) : null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const frequencyDelta = deltasEnabled
        ? (params.row.metrics?.delta?.frequencyPerDay ?? null)
        : null;
      const flakeScore = params.row.metrics?.flakeScore ?? null;
      const flakeScoreDelta = deltasEnabled
        ? (params.row.metrics?.delta?.flakeScore ?? null)
        : null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationDelta = deltasEnabled
        ? (params.row.metrics?.delta?.averageDurationMs ?? null)
        : null;
      const avgDurationThresholdValue = avgDuration === null ? null : avgDuration / 1000;
      const thresholdGroup = resolvedThresholds(params.row);
      const passRateTone = mapChipKeyToStatusTone(
        getThresholdChipKey(passRate, thresholdGroup.passRate),
      );
      const frequencyTone = mapChipKeyToStatusTone(
        getThresholdChipKey(frequency, thresholdGroup.frequency),
      );
      const flakeScoreTone = mapChipKeyToStatusTone(
        getThresholdChipKey(flakeScore, thresholdGroup.flakeScore),
      );
      const avgDurationTone = mapChipKeyToStatusTone(
        getThresholdChipKey(avgDurationThresholdValue, thresholdGroup.avgDuration),
      );

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: getChartTitle('passRate'),
                valueLabel:
                  passRate === null ? getStatusCopy('noData') : `${formatPercent(passRate)}%`,
                deltaValue: passRate === null ? null : passRateDelta,
                deltaLabel:
                  passRate === null || passRateDelta === null
                    ? null
                    : `${passRateDelta > 0 ? '+' : ''}${formatPercent(passRateDelta)}%`,
                isMissing: passRate === null,
                tone: passRateTone,
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: getChartTitle('frequency'),
                valueLabel:
                  frequency === null ? getStatusCopy('noData') : formatFrequency(frequency),
                deltaValue: frequency === null ? null : frequencyDelta,
                deltaLabel:
                  frequency === null || frequencyDelta === null
                    ? null
                    : `${frequencyDelta > 0 ? '+' : frequencyDelta < 0 ? '-' : ''}${formatFrequency(
                        Math.abs(frequencyDelta),
                      )}`,
                isMissing: frequency === null,
                tone: frequencyTone,
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: getChartTitle('avgDuration'),
                valueLabel:
                  avgDuration === null ? getStatusCopy('noData') : formatDuration(avgDuration),
                deltaValue: avgDuration === null ? null : avgDurationDelta,
                deltaLabel:
                  avgDuration === null || avgDurationDelta === null
                    ? null
                    : `${avgDurationDelta > 0 ? '+' : '-'}${formatDuration(
                        Math.abs(avgDurationDelta),
                        { roundMillisecondsWithSeconds: true },
                      )}`,
                isMissing: avgDuration === null,
                tone: avgDurationTone,
                direction: thresholdGroup.avgDuration.direction,
              },
              {
                key: 'flakeScore',
                label: getChartTitle('flakeScore'),
                valueLabel:
                  flakeScore === null ? getStatusCopy('noData') : `${formatPercent(flakeScore)}%`,
                deltaValue: flakeScore === null ? null : flakeScoreDelta,
                deltaLabel:
                  flakeScore === null || flakeScoreDelta === null
                    ? null
                    : `${flakeScoreDelta > 0 ? '+' : ''}${formatPercent(flakeScoreDelta)}%`,
                isMissing: flakeScore === null,
                tone: flakeScoreTone,
                direction: thresholdGroup.flakeScore.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<PipelineListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: columnWidths.actions.flex,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => (
      <TableActionsCell
        rowName={params.row.name}
        editActionLabel={getEntityActionLabel('edit', 'Pipeline')}
        deleteActionLabel={getEntityActionLabel('delete', 'Pipeline')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableEdit={editRowPendingId === params.row.id || deleteRowPendingId === params.row.id}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [
    nameColumn,
    latestStatusColumn,
    stagesColumn,
    buildsColumn,
    runsColumn,
    metricsColumn,
    ...(includeActionsColumn ? [actionsColumn] : []),
  ];
};
